import React from 'react';
import {
  LayoutDashboard, Hash, Megaphone, ShieldCheck, Smartphone, Crown, CalendarCheck, ScreenShare,
  Eye, FileSearch, HardDrive, Ban, Monitor, DownloadCloud, BellRing, ScrollText,
} from 'lucide-react';
import { User } from '../../types';

/**
 * 관리자 콘솔 메뉴 정의 — 메뉴 이름·묶음·설명·노출 역할의 유일한 출처.
 *
 * 새 관리 화면을 붙일 때: AdminPage에 키 추가 → 알맞은 묶음(ADMIN_NAV)에 항목 추가 → AdminConsole의
 * 패널 스위치에 컴포넌트 연결. 필요하면 '한눈에 보기'(AdminOverview)에 칸을 더한다.
 * key는 저장되지 않으므로 이름을 바꿔도 되지만, CLAUDE.md·도움말이 메뉴 이름(label)을 가리키는 곳이 있다.
 */

export type AdminPage =
  | 'overview'
  | 'channels' | 'messenger'
  | 'loginApprovals' | 'devices'
  | 'roles' | 'calendarEditors' | 'remoteSupport'
  | 'audit' | 'auditLogs' | 'consent'
  | 'uploadPolicy' | 'fileExtensions'
  | 'clients' | 'deploy' | 'push';

/**
 * 노출에 필요한 최소 전역 역할. 최종 판정은 서버(requireAuditor/requireSuperAdmin)가 하므로
 * 여기서는 "눌러도 403이 날 메뉴"를 미리 숨기는 것뿐이다.
 */
export type MinRole = 'auditor' | 'super_admin';

export interface AdminNavItem {
  key: AdminPage;
  label: string;
  icon: React.ElementType;
  /** 페이지 머리에 붙는 한 줄 설명(마침표 없음) */
  description: string;
  minRole?: MinRole;
}

export interface AdminNavGroup {
  /** 묶음 제목. null이면 제목 없이 맨 위에 놓인다('한눈에 보기') */
  label: string | null;
  items: AdminNavItem[];
}

export const ADMIN_NAV: AdminNavGroup[] = [
  {
    label: null,
    items: [
      { key: 'overview', label: '한눈에 보기', icon: LayoutDashboard, description: '처리할 일과 주요 설정 상태를 모아 봅니다' },
    ],
  },
  {
    label: '대화방 운영',
    items: [
      { key: 'channels', label: '공개 채널', icon: Hash, description: '전 직원이 함께 보는 공개 채널을 만들고 보관합니다' },
      { key: 'messenger', label: '공지 발송', icon: Megaphone, description: '공개 채널에 관리자 공지를 바로 보내거나 예약합니다' },
    ],
  },
  {
    label: '접속 보안',
    items: [
      { key: 'loginApprovals', label: '로그인 승인', icon: ShieldCheck, description: '로그인할 수 있는 직원 명단과 승인제 스위치를 관리합니다' },
      // 기기 신뢰 — 웹 브라우저 차단·PC 앱 기기 키·모바일 기기 승인(server/services/deviceTrust.js). 승인은 super_admin만
      { key: 'devices', label: '기기 승인', icon: Smartphone, minRole: 'super_admin', description: '웹·모바일·PC 앱 기기의 로그인을 승인하고 해제합니다' },
    ],
  },
  {
    label: '권한',
    items: [
      { key: 'roles', label: '역할 관리', icon: Crown, minRole: 'super_admin', description: '관리자·감사자·Super Admin 역할을 부여하고 회수합니다' },
      { key: 'calendarEditors', label: '회사 일정 권한', icon: CalendarCheck, minRole: 'super_admin', description: '자기 계열사 회사 일정을 올릴 수 있는 담당자를 정합니다' },
      { key: 'remoteSupport', label: '원격지원', icon: ScreenShare, minRole: 'super_admin', description: '원격지원 정책과 담당자, 지원 기록을 관리합니다' },
    ],
  },
  {
    label: '감사',
    items: [
      { key: 'audit', label: '메시지 감사', icon: Eye, minRole: 'auditor', description: '사유를 적어 열람을 요청하고, 다른 Super Admin이 승인하면 정해진 시간 동안 조회합니다' },
      { key: 'auditLogs', label: '감사 로그', icon: FileSearch, minRole: 'super_admin', description: '관리 작업 기록을 시간순으로 보고 변조 여부를 검증합니다' },
      // 이용 고지·동의(services/consentDocs.js) — admin은 자기 계열사, 접속 기록은 Super Admin만(서버 판정)
      { key: 'consent', label: '이용 고지 동의', icon: ScrollText, description: '직원별 이용 고지 동의 이력과 다시 확인이 필요한 사람을 봅니다' },
    ],
  },
  {
    label: '첨부 파일',
    items: [
      { key: 'uploadPolicy', label: '첨부 정책', icon: HardDrive, description: '첨부 최대 용량·개수와 보관 기간을 정합니다' },
      { key: 'fileExtensions', label: '첨부 차단 확장자', icon: Ban, description: '채팅에 첨부할 수 없는 파일 확장자를 관리합니다' },
    ],
  },
  {
    label: 'PC 앱·알림',
    items: [
      { key: 'clients', label: '앱 버전', icon: Monitor, description: '사용자별 설치 앱 버전과 최근 접속 시각을 봅니다' },
      { key: 'deploy', label: '전사 배포', icon: DownloadCloud, minRole: 'super_admin', description: 'PC 앱 설치 허용 IP와 미설치 직원 현황을 관리합니다' },
      { key: 'push', label: '알림 발송 현황', icon: BellRing, description: '휴대폰·PC 알림 발송 결과와 최근 실패를 봅니다' },
    ],
  },
];

const ROLE_RANK: Record<string, number> = { admin: 1, auditor: 2, super_admin: 3 };

/**
 * role이 undefined(role 필드가 없던 시절의 저장 세션)면 필터하지 않는다 — 현행 유지.
 * 서버가 403으로 막으므로 보여도 새는 것은 없다.
 */
export const canSee = (role: User['role'], minRole?: MinRole) =>
  !minRole || role === undefined || (ROLE_RANK[role ?? ''] ?? 0) >= ROLE_RANK[minRole];

/** 역할로 거른 메뉴. 볼 항목이 없는 묶음은 제목까지 뺀다(admin에게 '감사' 묶음이 통째로 없다) */
export function visibleNav(role: User['role']): AdminNavGroup[] {
  return ADMIN_NAV
    .map(g => ({ ...g, items: g.items.filter(i => canSee(role, i.minRole)) }))
    .filter(g => g.items.length > 0);
}

export function findNavItem(key: AdminPage): AdminNavItem {
  for (const g of ADMIN_NAV) {
    const hit = g.items.find(i => i.key === key);
    if (hit) return hit;
  }
  return ADMIN_NAV[0].items[0];
}

export const ROLE_LABELS: Record<string, string> = {
  admin: '관리자',
  auditor: '감사자',
  super_admin: 'Super Admin',
};
